"use client";

import React, { useState, useRef, useEffect } from "react";

interface ExpandablePageDescriptionProps {
  content: string;
  className?: string;
  maxLines?: number;
}

export default function ExpandablePageDescription({
  content,
  className = "",
  maxLines = 3,
}: ExpandablePageDescriptionProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [needsExpansion, setNeedsExpansion] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<string>("auto");
  const [fullHeight, setFullHeight] = useState<string>("auto");
  const contentRef = useRef<HTMLDivElement>(null);
  const fullContentRef = useRef<HTMLParagraphElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let measureTimer: NodeJS.Timeout;

    // Use setTimeout to ensure DOM is fully rendered
    const timer = setTimeout(() => {
      if (contentRef.current && fullContentRef.current) {
        // Sync width of hidden element with visible element
        const visibleWidth = contentRef.current.offsetWidth;
        if (visibleWidth > 0) {
          fullContentRef.current.style.width = `${visibleWidth}px`;
        }

        // Wait a bit for width to apply, then measure
        measureTimer = setTimeout(() => {
          if (contentRef.current && fullContentRef.current) {
            // Measure heights after width is set
            const measuredCollapsedHeight = contentRef.current.scrollHeight;
            const measuredFullHeight = fullContentRef.current.scrollHeight;

            // Debug log
            if (
              typeof window !== "undefined" &&
              process.env.NODE_ENV === "development"
            ) {
              console.log("ExpandablePageDescription - Height Check:", {
                collapsedHeight: measuredCollapsedHeight,
                fullHeight: measuredFullHeight,
                difference: measuredFullHeight - measuredCollapsedHeight,
                needsExpansion:
                  measuredFullHeight > measuredCollapsedHeight + 2,
                visibleWidth,
              });
            }

            // Check if content exceeds collapsed height (reduced buffer for more sensitive detection)
            // Also check character count as fallback
            const charCount = content.length;
            const shouldExpand =
              measuredFullHeight > measuredCollapsedHeight + 2 ||
              charCount > 150;

            if (shouldExpand) {
              setNeedsExpansion(true);
              // Set heights for smooth animation (use measured values)
              setCollapsedHeight(`${measuredCollapsedHeight}px`);
              setFullHeight(`${measuredFullHeight}px`);
            } else {
              setNeedsExpansion(false);
            }

            // Force expansion if content is clearly long (fallback)
            if (charCount > 200) {
              setNeedsExpansion(true);
              setCollapsedHeight(`${measuredCollapsedHeight}px`);
              setFullHeight(`${measuredFullHeight}px`);
            }
          }
        }, 100);
      }
    }, 300);

    // Also check on window resize
    const handleResize = () => {
      const timer2 = setTimeout(() => {
        if (contentRef.current && fullContentRef.current) {
          const visibleWidth = contentRef.current.offsetWidth;
          if (visibleWidth > 0) {
            fullContentRef.current.style.width = `${visibleWidth}px`;
          }
          const measuredCollapsedHeight = contentRef.current.scrollHeight;
          const measuredFullHeight = fullContentRef.current.scrollHeight;
          setNeedsExpansion(measuredFullHeight > measuredCollapsedHeight + 2);
          // Update heights for animation
          if (measuredFullHeight > measuredCollapsedHeight + 2) {
            setCollapsedHeight(`${measuredCollapsedHeight}px`);
            setFullHeight(`${measuredFullHeight}px`);
          }
        }
      }, 100);
      return () => clearTimeout(timer2);
    };

    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(timer);
      if (measureTimer) {
        clearTimeout(measureTimer);
      }
      window.removeEventListener("resize", handleResize);
    };
  }, [content, maxLines]);

  const handleToggle = (
    e?: React.MouseEvent<HTMLButtonElement | HTMLSpanElement>
  ) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setIsExpanded(!isExpanded);
  };

  // Don't render if no content
  if (!content || content.trim().length === 0) {
    return null;
  }

  return (
    <div className="relative w-full">
      {/* Hidden element to measure full height */}
      <p
        ref={fullContentRef}
        className={`${className} pointer-events-none absolute -z-10 opacity-0`}
        style={{
          position: "absolute",
          visibility: "hidden",
          width: contentRef.current?.offsetWidth || "100%",
        }}
      >
        {content}
      </p>

      {/* Visible content */}
      <div className="relative">
        <div
          ref={wrapperRef}
          className="overflow-hidden transition-all duration-700 ease-in-out"
          style={{
            maxHeight: isExpanded ? fullHeight : collapsedHeight,
          }}
        >
          <div
            ref={contentRef}
            className={className}
            style={{
              display: !isExpanded ? "-webkit-box" : "block",
              WebkitLineClamp: !isExpanded ? maxLines : "none",
              WebkitBoxOrient: !isExpanded ? "vertical" : "initial",
              overflow: "visible",
              textOverflow: !isExpanded ? "ellipsis" : "clip",
            }}
          >
            {content}
          </div>
        </div>
        {!isExpanded && needsExpansion && (
          <button
            type="button"
            onClick={handleToggle}
            className="mt-2 block w-full cursor-pointer text-left font-semibold text-violet-950 transition-opacity hover:opacity-80"
          >
            Read more
          </button>
        )}
      </div>

      {needsExpansion && isExpanded && (
        <button
          type="button"
          onClick={handleToggle}
          className="mt-3 block w-full cursor-pointer text-left font-semibold text-violet-950 transition-opacity hover:opacity-80"
          aria-expanded={isExpanded}
          aria-label="Show less"
        >
          Show Less
        </button>
      )}
    </div>
  );
}
